import { useQuery } from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import projectKeys from '@/api/queries/project/keyFactory';
import { graphql } from '@/gql';
import { PaginationInput } from '@/gql/graphql';
import { PAGINATION_ARG_DEFAULTS } from '@payaca/shared-isomorphic/constants/graphql-pagination';

const GetProjectCommitments = graphql(`
  query GetProjectCommitments($projectId: ID!, $pagination: PaginationInput!) {
    projectById(id: $projectId) {
      servicePlanPeriod {
        commitments(pagination: $pagination) {
          items {
            __typename
            ... on TaskCommitment {
              id
              status
              task {
                ... on IEntity {
                  id
                }
              }
              template {
                __typename
                ... on ITaskTemplate {
                  taskName
                  taskNotes
                }
                ... on ChecklistTaskTemplate {
                  templateName
                  checklistItems
                }
                ... on FormTaskTemplate {
                  templateName
                  formTemplate {
                    id
                    name
                    pdfUrl
                  }
                }
                ... on LegacyFormTaskTemplate {
                  templateName
                  formTemplate {
                    id
                    name
                  }
                }
              }
            }
            ... on ScheduledEventCommitment {
              id
              status
              scheduledEvent {
                id
              }

              template {
                templateName
                eventName
                eventDescription
                duration
                includeCustomerInformation
                tags {
                  id
                }
                tasks {
                  __typename
                  ... on IScheduledEventTaskTemplate {
                    taskName
                    taskNotes
                  }
                  ... on ScheduledEventChecklistTaskTemplate {
                    checklistItems
                  }
                  ... on ScheduledEventFormTaskTemplate {
                    formTemplate {
                      id
                      name
                      pdfUrl
                    }
                  }
                  ... on ScheduledEventLegacyFormTaskTemplate {
                    formTemplate {
                      id
                      name
                    }
                  }
                  ... on ScheduledEventCustomFieldsTaskTemplate {
                    customFieldIdentifiers
                  }
                }
                uploads {
                  id
                }
              }
            }
          }
          totalCount
          offset
          limit
        }
      }
    }
  }
`);

const useGetProjectCommitments = (
  projectId?: string,
  pagination: PaginationInput = PAGINATION_ARG_DEFAULTS
) => {
  const { data, ...rest } = useQuery({
    enabled: !!projectId,
    queryKey: projectKeys.commitments(projectId!, pagination),
    queryFn: () => {
      return gqlClient.request(GetProjectCommitments, {
        projectId: projectId!,
        pagination,
      });
    },
  });

  return {
    commitments: data?.projectById.servicePlanPeriod?.commitments,
    data,
    ...rest,
  };
};

export default useGetProjectCommitments;
